import React, { useState, useEffect } from "react"
import { Mail, Phone, MapPin, ArrowRight, CheckCircle, AlertCircle, Loader } from "lucide-react"

const inputClass = "w-full bg-stone-900 border border-stone-800 text-stone-200 placeholder-stone-600 px-4 py-3.5 font-['Inter'] text-sm focus:outline-none focus:border-amber-400 transition-colors"
const labelClass = "block font-['Space_Grotesk'] text-[9px] tracking-[0.25em] uppercase mb-2"

// Read Rails CSRF token from the meta tag
function getCsrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
}

// Minimal hook for office locations
function useOffices() {
  const [offices, setOffices] = useState([])
  useEffect(() => {
    fetch("/api/office_locations")
      .then((r) => r.json())
      .then(setOffices)
      .catch(() => {})
  }, [])
  return offices
}

export default function Contact() {
  const offices = useOffices()
  const [form, setForm] = useState({
    name: "", mobile: "", projectCity: "", email: "", service: "", budget: "", message: "",
  })
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleReset = () => {
    setSubmitted(false)
    setError(null)
    setForm({ name: "", mobile: "", projectCity: "", email: "", service: "", budget: "", message: "" })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const res = await fetch("/api/inquiries", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        body: JSON.stringify({
          inquiry: {
            name:         form.name,
            mobile:       form.mobile,
            project_city: form.projectCity,
            email:        form.email,
            service:      form.service,
            budget:       form.budget,
            message:      form.message,
          },
        }),
      })

      const data = await res.json()

      if (data.success) {
        setSubmitted(true)
      } else {
        setError(data.error || "Something went wrong. Please try again.")
      }
    } catch (err) {
      setError("Network error. Please check your connection and try again.")
    } finally {
      setLoading(false)
    }
  }


  return (
    <main className="pt-24 min-h-screen bg-stone-950">
      {/* Header */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-5">
          <span className="w-6 h-px bg-amber-400" />
          Get In Touch
        </span>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left — info */}
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-5xl lg:text-7xl font-light text-stone-100 leading-tight mb-8">
              Let's build<br />
              <span className="italic text-stone-400">something together</span>
            </h1>
            <p className="font-['Inter'] text-sm text-stone-400 leading-relaxed mb-12 max-w-md">
              Whether you have a fully-formed brief or a half-formed idea, we want to hear from you. Tell us about your project and we'll arrange a no-obligation introductory call.
            </p>

            {/* Offices */}
            <div className="space-y-8">
              {offices.length === 0 ? (
                // Skeleton placeholders while loading
                [1, 2, 3].map((i) => (
                  <div key={i} className="border-l border-stone-800 pl-5 animate-pulse space-y-2">
                    <div className="h-2.5 bg-stone-800 w-1/3 rounded" />
                    <div className="h-2 bg-stone-800 w-2/3 rounded" />
                    <div className="h-2 bg-stone-800 w-1/2 rounded" />
                  </div>
                ))
              ) : (
                offices.map((office) => (
                  <div key={office.id ?? office.city} className="border-l border-stone-800 pl-5">
                    <h3 className="font-['Space_Grotesk'] text-[10px] tracking-[0.25em] text-amber-400 uppercase mb-2">
                      {office.city}, {office.country}
                    </h3>
                    <div className="space-y-1">
                      {office.address && (
                        <div className="flex items-start gap-2">
                          <MapPin size={12} className="text-stone-600 mt-0.5 shrink-0" />
                          <p className="font-['Inter'] text-xs text-stone-500">{office.address}</p>
                        </div>
                      )}
                      {office.phone && (
                        <div className="flex items-center gap-2">
                          <Phone size={12} className="text-stone-600 shrink-0" />
                          <p className="font-['Inter'] text-xs text-stone-500">{office.phone}</p>
                        </div>
                      )}
                      {office.email && (
                        <div className="flex items-center gap-2">
                          <Mail size={12} className="text-stone-600 shrink-0" />
                          <p className="font-['Inter'] text-xs text-stone-500">{office.email}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right — form */}
          <div>
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full py-24 text-center gap-6">
                <CheckCircle size={48} className="text-amber-400" />
                <h2 className="font-['Cormorant_Garamond'] text-4xl font-light text-stone-100">
                  Message received
                </h2>
                <p className="font-['Inter'] text-sm text-stone-400 max-w-xs">
                  Thank you for reaching out. We'll review your brief and be in touch within one business day.
                </p>
                <button
                  onClick={handleReset}
                  className="font-['Space_Grotesk'] text-[10px] tracking-[0.2em] text-amber-400 border border-amber-400 px-6 py-3 hover:bg-amber-400 hover:text-stone-950 transition-all"
                >
                  SEND ANOTHER
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">

                {/* Row 1 — Name (required) + Mobile (required) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={`${labelClass} text-amber-400`}>
                      Full Name <span className="text-amber-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className={`${labelClass} text-amber-400`}>
                      Mobile Number <span className="text-amber-400">*</span>
                    </label>
                    <input
                      type="tel"
                      name="mobile"
                      required
                      value={form.mobile}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="+880 000 000 0000"
                    />
                  </div>
                </div>

                {/* Row 2 — Project City (required) + Email (optional) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={`${labelClass} text-amber-400`}>
                      Project City <span className="text-amber-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="projectCity"
                      required
                      value={form.projectCity}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="e.g. Dhaka, Dubai"
                    />
                  </div>
                  <div>
                    <label className={`${labelClass} text-stone-500`}>
                      Email Address <span className="text-stone-600">(optional)</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                {/* Row 3 — Service (optional) + Budget (optional) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={`${labelClass} text-stone-500`}>
                      Service Required <span className="text-stone-600">(optional)</span>
                    </label>
                    <select
                      name="service"
                      value={form.service}
                      onChange={handleChange}
                      className="w-full bg-stone-900 border border-stone-800 text-stone-400 px-4 py-3.5 font-['Inter'] text-sm focus:outline-none focus:border-amber-400 transition-colors appearance-none cursor-pointer"
                    >
                      <option value="">Select a service</option>
                      <option>Architecture</option>
                      <option>Interior Design</option>
                      <option>3D Visualization</option>
                      <option>Urban Planning</option>
                      <option>Multiple Services</option>
                    </select>
                  </div>
                  <div>
                    <label className={`${labelClass} text-stone-500`}>
                      Project Budget <span className="text-stone-600">(optional)</span>
                    </label>
                    <select
                      name="budget"
                      value={form.budget}
                      onChange={handleChange}
                      className="w-full bg-stone-900 border border-stone-800 text-stone-400 px-4 py-3.5 font-['Inter'] text-sm focus:outline-none focus:border-amber-400 transition-colors appearance-none cursor-pointer"
                    >
                      <option value="">Select a budget range</option>
                      <option>Under $50,000</option>
                      <option>$50,000 – $150,000</option>
                      <option>$150,000 – $500,000</option>
                      <option>$500,000 – $1M</option>
                      <option>Over $1M</option>
                    </select>
                  </div>
                </div>

                {/* Message (optional) */}
                <div>
                  <label className={`${labelClass} text-stone-500`}>
                    Tell Us About Your Project <span className="text-stone-600">(optional)</span>
                  </label>
                  <textarea
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    className="w-full bg-stone-900 border border-stone-800 text-stone-200 placeholder-stone-600 px-4 py-3.5 font-['Inter'] text-sm focus:outline-none focus:border-amber-400 transition-colors resize-none"
                    placeholder="Describe your project, timeline, and any key requirements..."
                  />
                </div>

                {/* Required note */}
                <p className="font-['Inter'] text-[11px] text-stone-600">
                  Fields marked <span className="text-amber-400">*</span> are required.
                </p>

                {/* Error message */}
                {error && (
                  <div className="flex items-start gap-3 bg-red-950/40 border border-red-900 px-4 py-3">
                    <AlertCircle size={15} className="text-red-400 mt-0.5 shrink-0" />
                    <p className="font-['Inter'] text-sm text-red-300">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 bg-amber-400 text-stone-950 py-4 font-['Space_Grotesk'] text-xs tracking-[0.2em] font-medium hover:bg-amber-300 transition-colors group disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader size={14} className="animate-spin" />
                      SENDING...
                    </>
                  ) : (
                    <>
                      SEND YOUR BRIEF
                      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>

                <p className="font-['Inter'] text-[11px] text-stone-600 text-center leading-relaxed">
                  We typically respond within 24 hours. Your information is kept strictly confidential.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </main>
  )
}
